import { HeaderMenuItem } from '../models/header-menu.model';

/**
 * Lucide equivalents for submenu items, keyed by visible text. The raw menu
 * data (app-data.json) sets `fas fa-cog` on almost every Config item, which
 * rendered as a wall of identical gears. Where a text match exists here the
 * header menu template renders i-lucide instead of <i class>.
 *
 * Every direct child of the Admin and Global Admin menus must resolve through
 * {@link resolveSubmenuLucideIcon}: a row that falls back to its FontAwesome
 * class keeps that glyph's intrinsic width instead of the fixed 20px Lucide
 * slot and its label sits ~6px off its siblings (ENG-1894, PR 30941).
 * `header-menu.icons.spec.ts` enforces this against app-data.json.
 */
export const SUBMENU_LUCIDE_ICON_MAP: Record<string, string> = {
  // New
  'New Lead': 'flag',
  'New Agent': 'user-round',
  'New Contact(s)': 'contact',
  'New Application': 'building-2',

  // My Lists
  Leads: 'flag',
  Agents: 'user-round',
  Contacts: 'contact',
  Applications: 'building-2',
  'Contact Lists': 'list',
  'External Organizations': 'users',
  'External Org. Users': 'user-check',

  // My Tools
  Dialer: 'phone',
  'Quick Pricer': 'dollar-sign',
  'Make Contact AI': 'mail-plus',
  'Broadcast Manager': 'megaphone',
  'Support Tickets': 'life-buoy',
  'Payment Calculator': 'calculator',

  // Reporting
  Reports: 'bar-chart-3',
  'Reports (All Companies)': 'bar-chart-3',
  'Reporting Dashboard': 'layout-dashboard',
  'KPI Dashboard': 'gauge',
  'Dialer Dashboard': 'phone-call',
  'Pipeline Metrics': 'kanban',
  'Pipeline Waterfall': 'bar-chart-3',
  'Task Metrics': 'square-check',
  'Sales Report': 'trending-up',
  'Mortgage Call Report': 'phone',
  'Profitability Report': 'dollar-sign',
  'Messaging Report': 'message-square',
  'VICI Dialer Report': 'phone-outgoing',

  // Global Admin
  Companies: 'building-2',
  'Release Notes': 'newspaper',
  'Billing Report': 'receipt',
  'LOS Test Data': 'database',
  'Email Templates': 'mail',
  'SMS Templates': 'message-square',
  'Credit Config': 'credit-card',
  'Document Types': 'files',
  'AI Orchestration': 'bot',
  'Copy Config': 'copy',
  'Underwriting Rule Catalog': 'landmark',
  'Company Config Export Import': 'arrow-right-left',
  'Underwriting Guides': 'book',

  // Admin
  Wizard: 'wand',
  Company: 'building-2',
  'External Company Management': 'building',
  'Lead Config': 'flag',
  'Routing Config': 'route',
  'Loan Config': 'banknote',
  'Agent Config': 'user-round-cog',
  'Voice / SMS Config': 'phone-call',
  'LOS Config': 'plug',
  'OCR Config': 'scan-text',
  'Document Config': 'file-text',
  'Task Config': 'clipboard-list',
  'Email Config': 'mail',
  'Lender Config': 'landmark',
  'Loan Beam Config': 'scan-search',
  'Reporting Config': 'bar-chart-3',
  'TPO Config': 'handshake',
  'Configuration Issues': 'triangle-alert',
  'Webhook Subscriptions': 'webhook',
  'Tag Management': 'tags',
  'Agentic Profiles': 'bot',
  'Agentic Workflows': 'workflow',
  'Underwriting Configuration': 'clipboard-check',
  'System Level': 'sliders-horizontal',
  'Application Settings': 'settings-2',

  // Recently Visited
  'Recent Apps': 'folder',
  'Recent Leads': 'flag',
  'Recent Agents': 'user-round',

  // Admin > Company
  Invite: 'user-plus',
  'App Roles': 'key-round',
  'Admin Roles': 'shield-check',
  Users: 'users',
  Branches: 'map-pin',
  'Org Chart': 'network',
  Alignment: 'layers',
  'Brand Configuration': 'palette',
  'External Brand Configuration': 'paintbrush',

  // Admin > Lead Config
  'Lead Status': 'circle-check',
  'Lead Tags': 'tag',
  'Lead Campaigns': 'megaphone',
  'Import Leads': 'upload',

  // Admin > Routing Config
  'Lead Routing': 'git-fork',
  Redistribution: 'shuffle',
  Fetch: 'download',
  'Warm Transfer': 'phone-forwarded',
  'Inbound Call Routing': 'phone-incoming',
  'Short App Links': 'link',

  // Admin > Loan Config
  'Loan Status': 'circle-check',
  'Sub Status': 'circle-dot',
  'Loan Type': 'layers',
  'Loan Purpose': 'target',
  'Adverse Reason': 'ban',
  'Key Dates': 'calendar',
  'CoC Reasons': 'file-x',
  'URLA Validations': 'shield-check',

  // Admin > Agent Config
  'Agent Type': 'user-round',
  'Agent Tag Management': 'tag',
  'Import Agents': 'upload',
  'Service Providers': 'briefcase',

  // Admin > Voice / SMS Config
  'Dial Lists': 'list',
  'Phone Numbers': 'hash',
  'Number Pools': 'layers',
  'Do Not Email List': 'mail-x',
  'Do Not Call List': 'phone-off',

  // Admin > LOS Config
  'LOS Onboarding': 'play',
  'LOS Mappings': 'arrow-right-left',
  'LOS Field Filtering': 'funnel',
  'Configuration Monitoring': 'activity',
  Disclosures: 'file-check',

  // Admin > OCR Config / Loan Beam Config
  'Document Type Mapping': 'file-search',

  // Admin > Document Config
  'Document Generation': 'file-plus',
  'Document Type': 'file-text',
  'Global Document Types': 'files',
  'Stacking Order': 'list-ordered',

  // Admin > Task Config
  'Lead Task Management': 'flag',
  'Loan Task Management': 'clipboard-check',
  Characteristics: 'tag',
  'Loan Characteristic Tasks': 'list-todo',
  'Borrower Characteristic Tasks': 'user-check',
  Checklists: 'list-checks',
  Questions: 'message-circle-question-mark',

  // Admin > Email Config
  'Email Campaigns': 'send',
  'Email Disclaimers': 'shield-alert',
  'Email Template Image Library': 'image',

  // Admin > Lender Config
  Lenders: 'landmark',
  Products: 'package',
  'Fees Library': 'library',
  'Fee Restrictions': 'ban',
  'Fee Override Approvals': 'circle-check',
  'Fee Templates': 'receipt',
  'County Calculation Rates': 'percent',
  'Closing Cost Templates': 'file-text',

  // Admin > Reporting Config
  'KPI Dashboard Config': 'gauge',
  'Mortgage Call Reports Config': 'phone',
  'Goals Config': 'target',

  // Admin > Agentic Profiles
  Profiles: 'user-round-cog',
  Guardrails: 'shield-check',
  'Eval Studio': 'flask-conical',
  'Eval Scenarios': 'clipboard-check',
};

/**
 * Fallback for submenu items whose text we don't hardcode — matches their
 * FontAwesome class to a Lucide equivalent. Picks up the dynamic children
 * under "Recent" (recent leads/agents/apps) whose text is free-form.
 */
export const FA_TO_LUCIDE_FALLBACK: Record<string, string> = {
  'fa-folder': 'folder',
  'fa-flag': 'flag',
  'fa-user-tie': 'user-round',
  'fa-user': 'user-round',
  'fa-users': 'users',
  'fa-star': 'history',
};

export const resolveSubmenuLucideIcon = (item: Pick<HeaderMenuItem, 'text' | 'icon'> | null | undefined): string | null => {
  if (!item) return null;
  const byText = item.text ? SUBMENU_LUCIDE_ICON_MAP[item.text] : undefined;
  if (byText) return byText;
  if (!item.icon) return null;
  for (const faFragment of Object.keys(FA_TO_LUCIDE_FALLBACK)) {
    if (item.icon.includes(faFragment)) return FA_TO_LUCIDE_FALLBACK[faFragment];
  }
  return null;
};
